// Getting in.
//
// This API has no login route, on purpose. `docs/ARCHITECTURE.md` §1 fixes token-based auth
// against an identity vendor, and `packages/auth` verifies a token the vendor minted —
// there is no password anywhere in this system and no endpoint that would take one.
//
// With `VITE_CLERK_PUBLISHABLE_KEY` built in, this screen mounts the vendor's own
// component — Google, Apple, an email code — and `clerk.ts` writes the session it
// establishes into `session.ts`, at which point the router swaps this route away.
// Nothing else in this package changed when that arrived, which is what the previous
// version of this comment promised.
//
// TWO MODES, ONE SCREEN. Somebody arriving at Mor with no session is more likely to be new
// than returning — an invitee already has `Invite.tsx`, and everyone else is meeting the
// product for the first time — so `/` and every other route redirect signed-out to
// `/signup`, which mounts the vendor's sign-up. `/signin` stays for the returning person,
// and each card is told where the other one lives (`signInUrl`, `signUpUrl`), so the
// vendor's own "already have an account" and "sign up" links move between these two routes
// instead of the vendor's default `/sign-in` and `/sign-up`, which this app does not serve.
// Both wear `screen--signin`, because `styles.css` dresses the vendor's controls through
// that class and the two cards are the same card.
//
// Without the key, the fallback is the one honest thing a client can do without
// inventing a route: accept the vendor's token, pasted, and hold it. There is no such
// thing as signing up without the vendor, so both routes show that same form. The suites
// and a vendorless dev loop run this way.

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { clerkConfigured, getClerk } from '../clerk.js';
import { session } from '../session.js';
import { vendorAppearance } from '../vendor-appearance.js';

type Mode = 'in' | 'up';

function VendorCard({ mode }: { mode: Mode }): JSX.Element {
  const mount = useRef<HTMLDivElement | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const element = mount.current;
    const vendor = getClerk();
    if (element === null || vendor === null) {
      return undefined;
    }
    let unmounted = false;
    const props = {
      appearance: vendorAppearance(),
      signInUrl: '/signin',
      signUpUrl: '/signup',
    };
    void vendor
      .then((clerk) => {
        if (!unmounted) {
          if (mode === 'up') {
            clerk.mountSignUp(element, props);
          } else {
            clerk.mountSignIn(element, props);
          }
        }
      })
      .catch(() => {
        if (!unmounted) {
          setFailed(true);
        }
      });
    return () => {
      unmounted = true;
      void vendor
        .then((clerk) => {
          if (mode === 'up') {
            clerk.unmountSignUp(element);
          } else {
            clerk.unmountSignIn(element);
          }
        })
        .catch(() => undefined);
    };
  }, [mode]);

  return (
    <main className="screen screen--signin">
      <h1 className="screen__title">{mode === 'up' ? 'Sign up' : 'Sign in'}</h1>
      <div ref={mount} />
      {failed ? (
        <p className="note" role="status">
          {mode === 'up' ? 'Sign-up' : 'Sign-in'} didn’t load. Check the connection and
          reload this page.
        </p>
      ) : null}
    </main>
  );
}

function Entry({ mode }: { mode: Mode }): JSX.Element {
  const [value, setValue] = useState('');
  const navigate = useNavigate();

  if (clerkConfigured()) {
    return <VendorCard mode={mode} />;
  }

  return (
    <main className="screen screen--signin">
      <h1 className="screen__title">Sign in</h1>
      <form
        className="signin"
        onSubmit={(event) => {
          event.preventDefault();
          if (value.trim() === '') {
            return;
          }
          session.signIn(value);
          navigate('/');
        }}
      >
        <label className="signin__label" htmlFor="signin-token">
          Sign-in token
        </label>
        <input
          id="signin-token"
          className="signin__field"
          value={value}
          autoComplete="off"
          onChange={(event) => setValue(event.target.value)}
        />
        <button
          type="submit"
          className="button button--primary"
          disabled={value.trim() === ''}
        >
          Continue
        </button>
      </form>
      <p className="note">
        This prototype takes the token your identity provider issued. It is kept for this
        browser tab only.
      </p>
    </main>
  );
}

export function SignIn(): JSX.Element {
  return <Entry mode="in" />;
}

export function SignUp(): JSX.Element {
  return <Entry mode="up" />;
}
